import { useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { dayLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Star, Plus, Trash2, Upload, Loader2, FileText, Lock } from "lucide-react";

export const receiptUrl = (r) => `${API}/report-receipts/${r.id}/download?auth=${localStorage.getItem("sc_token")}`;

export function Stars({ value, onChange, size = "w-7 h-7" }) {
  return (
    <div className="flex gap-1" data-testid="report-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={`report-star-${n}`}>
          <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
        </button>
      ))}
    </div>
  );
}

function Receipts({ list, setList }) {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files)) {
      if (list.length >= 10) { toast.error("Massimo 10 ricevute"); break; }
      const fd = new FormData();
      fd.append("file", f);
      try { const r = await api.post("/reports/receipts", fd); list = [...list, r.data]; setList(list); }
      catch (err) { toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail)}`); }
    }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      {list.map((r) => (
        <div key={r.id} className="flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm" data-testid={`report-receipt-${r.id}`}>
          <FileText className="w-4 h-4 text-cyan-400 shrink-0" /><a href={receiptUrl(r)} target="_blank" rel="noopener noreferrer" className="truncate flex-1">{r.filename}</a>
          <button type="button" onClick={() => setList(list.filter((x) => x.id !== r.id))} data-testid={`report-receipt-remove-${r.id}`}><Trash2 className="w-4 h-4 text-rose-400" /></button>
        </div>
      ))}
      <label className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-600 px-3 py-3 text-sm text-slate-300 cursor-pointer hover:bg-slate-800/60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "Caricamento…" : "Carica ricevute (foto o PDF)"}
        <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="report-receipt-input" disabled={busy}
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      </label>
    </div>
  );
}

function Expenses({ list, setList }) {
  const upd = (i, k, v) => setList(list.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  return (
    <div className="space-y-2">
      {list.map((e, i) => (
        <div key={i} className="flex gap-2">
          <Input data-testid={`report-expense-desc-${i}`} value={e.description} onChange={(x) => upd(i, "description", x.target.value)} placeholder="Es. carburante, pranzo" className="bg-slate-800 border-slate-700" />
          <Input data-testid={`report-expense-amount-${i}`} type="text" inputMode="decimal" value={e.amount} onChange={(x) => upd(i, "amount", x.target.value)} placeholder="€" className="bg-slate-800 border-slate-700 w-24" />
          <button type="button" data-testid={`report-expense-remove-${i}`} className="px-1" onClick={() => setList(list.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-rose-400" /></button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" data-testid="report-expense-add" onClick={() => setList([...list, { description: "", amount: "" }])} className="border-slate-700"><Plus className="w-4 h-4 mr-1" /> Aggiungi spesa</Button>
    </div>
  );
}

function ReportForm({ group, report, onDone }) {
  const [days, setDays] = useState(() => group.map((d) => report?.days?.find((x) => x.date === d.date) || { date: d.date, start: d.slot_start || "", end: d.slot_end || "" }));
  const [rating, setRating] = useState(report?.rating || 0);
  const [problems, setProblems] = useState(report?.problems || "");
  const [notes, setNotes] = useState(report?.notes || "");
  const [expenses, setExpenses] = useState(report?.expenses || []);
  const [receipts, setReceipts] = useState(report?.receipts || []);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!rating) { toast.error("Dai un voto con le stelle"); return; }
    if (expenses.some((e) => !String(e.description).trim() || isNaN(parseFloat(String(e.amount).replace(",", "."))))) { toast.error("Completa descrizione e importo di ogni spesa"); return; }
    if (!window.confirm("Inviare il report? Dopo l'invio non potrai più modificarlo.")) return;
    setBusy(true);
    try {
      await api.post(`/events/${group[0].event_id}/report`, { days, rating, problems, notes, receipt_ids: receipts.map((r) => r.id),
        expenses: expenses.map((e) => ({ description: e.description, amount: parseFloat(String(e.amount).replace(",", ".")) || 0 })) });
      toast.success("Report inviato al manager");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sec = "text-xs uppercase tracking-wider font-mono text-slate-500 mb-1.5";
  return (
    <div className="space-y-4">
      <div><p className={sec}>Orari effettivi</p>
        {days.map((d, i) => (
          <div key={d.date} className="flex items-center gap-2 mb-1.5">
            <span className="text-sm w-24 shrink-0">{dayLabel(d.date)}</span>
            <Input type="time" data-testid={`report-start-${d.date}`} value={d.start || ""} onChange={(e) => setDays(days.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className="bg-slate-800 border-slate-700" />
            <Input type="time" data-testid={`report-end-${d.date}`} value={d.end || ""} onChange={(e) => setDays(days.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} className="bg-slate-800 border-slate-700" />
          </div>
        ))}</div>
      <div><p className={sec}>Andamento dell'evento</p><Stars value={rating} onChange={setRating} /></div>
      <div><p className={sec}>Problemi durante l'evento</p><Textarea data-testid="report-problems" value={problems} onChange={(e) => setProblems(e.target.value)} placeholder="Descrivi eventuali problemi (lascia vuoto se nessuno)" className="bg-slate-800 border-slate-700" /></div>
      <div><p className={sec}>Note</p><Textarea data-testid="report-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Altre note (opzionale)" className="bg-slate-800 border-slate-700" /></div>
      <div><p className={sec}>Rimborsi spese</p><Expenses list={expenses} setList={setExpenses} /></div>
      <div><p className={sec}>Ricevute</p><Receipts list={receipts} setList={setReceipts} /></div>
      <Button data-testid="report-submit" disabled={busy} onClick={send} className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia report"}</Button>
    </div>
  );
}

export function EventReportButton({ group, report, onDone }) {
  const [open, setOpen] = useState(false);
  const locked = report?.locked;
  return (
    <>
      <Button data-testid={`report-open-${group[0].event_id}`} disabled={locked} onClick={() => setOpen(true)}
        className={`w-full mt-3 font-semibold ${locked ? "bg-slate-800 text-slate-400" : "bg-amber-400 hover:bg-amber-300 text-slate-950"}`}>
        {locked ? <><Lock className="w-4 h-4 mr-1.5" /> Report inviato</> : <><ClipboardCheck className="w-4 h-4 mr-1.5" /> {report ? "Correggi e reinvia il report" : "Compila il report di fine evento"}</>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto w-[calc(100%-1.5rem)] px-4 sm:px-6" data-testid="report-dialog">
          <DialogHeader><DialogTitle>Report fine evento</DialogTitle><DialogDescription className="text-slate-400">{group[0].event_name || "Evento"}</DialogDescription></DialogHeader>
          <ReportForm group={group} report={report} onDone={() => { setOpen(false); onDone(); }} />
        </DialogContent>
      </Dialog>
    </>
  );
}
